import { LabelMesh } from './LabelMesh';
import { LabelMeshType, Vector3, Quaternion } from '../types';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import * as THREE from 'three';
import { CSS3DObject } from 'three/examples/jsm/renderers/CSS3DRenderer.js';
import { POI } from '../poi/POI';
import { Scene } from '../core/Scene';

/**
 * HTMLLabelMesh - HTML-based text rendering using CSS3D
 * Follows proper OOP hierarchy extending LabelMesh
 * Uses marked library for proper Markdown→HTML conversion
 */
export class HTMLLabelMesh extends LabelMesh {
    private _element: HTMLElement | null = null;
    private _cssObject: CSS3DObject | null = null;
    
    // 🚫 REMOVED: Experimental aspect ratio system - unified with LabelMesh scaling

    constructor(poi: POI) {
        super(poi, 'HTMLLabelMesh');
        this.initializeMarkdownRenderer();
        
        // ✅ Transform now has proper defaults thanks to Transform.createInheritable() fix
        //console.log(`✅ HTMLLabelMesh: Transform auto-initialized with defaults: pos=${this.transform.position.value}, rot=${this.transform.rotation.value}, scale=${this.transform.scale.value}`);
        
        // 🎯 Listen to labelInteractive property canges
        this.poi.poidata.labelInteractive.on(() => {
            this.updatePointerEvents();
        });
    }

    /**
     * Initialize the markdown renderer with custom settings
     */
    private initializeMarkdownRenderer(): void {
        // Configure marked for label rendering
        marked.setOptions({
            breaks: true,        // Convert \n to <br>
            gfm: true           // GitHub Flavored Markdown
        });
    }

    /**
     * Initialize the HTML mesh
     */
    async initialize(): Promise<void> {
        try {
            // Create HTML element
            this._element = document.createElement('div');
            this._element.style.userSelect = 'none';
            this._element.style.position = 'absolute';
            this._element.style.display = 'inline-block';
            
            // 🎯 Configure pointer events based on labelInteractive setting
            this.updatePointerEvents();
            
            this._isInitialized = true;
            this.updateContent(this.poi.poidata.html.value ?? '');
            this.updateStyle();
            
            // Create CSS3DObject and add to scene
            const cssObject = this.createCSS3DObject();
            if (cssObject) {
                // Store reference for scene integration
                this._mesh = cssObject as any; // CSS3DObject acts as our mesh
                
                // Add to CSS3D scene immediately after creation
                this.addToScene();
                
                // 🚫 REMOVED: updateGeometryFromContent() - let base class handle scaling
                
                //console.log(`✅ HTMLLabelMesh initialized for ${this.poi.name}`);
            }
            
        } catch (error) {
            console.error('Failed to initialize HTMLLabelMesh:', error);
            throw error;
        }
    }



    /**
     * Update style properties
     */
    updateStyle(): void {
        if (!this._element || !this._isInitialized) return;

        const fontSize = this.poi.poidata.labelFontSize.value ?? 14;
        const fontFamily = this.poi.poidata.labelFontFamily.value ?? 'Arial';
        const textColor = (this.poi.poidata.labelColor.value ?? new THREE.Color('#ffffff')).toString();
        const backgroundColor = (this.poi.poidata.labelBackgroundColor.value ?? new THREE.Color('#000000')).toString();
        const backgroundGradient = this.poi.poidata.labelBackgroundGradient.value;
        
        // Default to 'html' mode if the value is null
        const renderMode = this.poi.poidata.labelRenderMode.value ?? 'html';
        const textBackgroundMode = this.poi.poidata.labelBackgroundMode.value || 'line';

        // Apply base styles
        this._element.style.fontFamily = fontFamily;
        this._element.style.fontSize = `${fontSize}px`;
        this._element.style.color = textColor;
        
        // 🚫 REMOVED: Manual scaling - let LabelMesh._updateScale() handle this
        // Scale is now handled by the base class scaling system

        // Add label-specific CSS classes for styling
        this._element.className = 'html-label-mesh';

        // 🆕 Apply developer-adjustable max-width if provided (>0)
        const maxWidth = this.poi.poidata.labelMaxWidth?.value ?? 0;
        if (typeof maxWidth === 'number' && maxWidth > 0) {
            this._element.style.maxWidth = `${maxWidth}px`;
            this._element.style.width = 'auto';
            this._element.style.whiteSpace = 'normal';
            this._element.style.wordWrap = 'break-word';
        } else {
            // Let content flow if not set
            this._element.style.maxWidth = '';
        }

        if (textBackgroundMode !== 'line') {
            // Block mode - background on entire element
            if (backgroundGradient && backgroundGradient.trim()) {
                this._element.style.background = backgroundGradient; // Use gradient
                this._element.style.backgroundColor = ''; // Clear solid color
            } else {
                this._element.style.backgroundColor = backgroundColor;
                this._element.style.background = ''; // Clear gradient
            }
            this._element.style.padding = '10px';
            this._element.style.borderRadius = '5px';
        } else {
            // Line mode - background on individual elements
            this._element.style.backgroundColor = 'transparent';
            this._element.style.background = '';
            this._element.style.padding = '0';
            
            const elements = this._element.querySelectorAll('h1, h2, h3, h4, h5, h6, p, li, blockquote, pre');
            elements.forEach(elm => {
                (elm as HTMLElement).style.margin = '0.2em 0';
                if (backgroundGradient && backgroundGradient.trim()) {
                    (elm as HTMLElement).style.background = backgroundGradient;
                    (elm as HTMLElement).style.backgroundColor = '';
                } else {
                    (elm as HTMLElement).style.backgroundColor = backgroundColor;
                    (elm as HTMLElement).style.background = '';
                }
                (elm as HTMLElement).style.padding = '2px 8px';
                (elm as HTMLElement).style.borderRadius = '3px';
                (elm as HTMLElement).style.display = 'table';
            });
        }
        
        // 🎯 Update pointer events when style changes
        this.updatePointerEvents();
    }
    
    /**
     * 🎯 Configure pointer events based on labelInteractive setting
     * This controls whether labels block camera controls or allow click-through
     */
    private updatePointerEvents(): void {
        if (!this._element) return;
        
        const isInteractive = this.poi.poidata.labelInteractive.value ?? false;
        
        if (isInteractive) {
            // ✅ Label is interactive - allows clicks, hovers, etc.
            this._element.style.pointerEvents = 'auto';
            this._element.style.cursor = 'pointer';
            //console.log(`🖱️ HTMLLabel ${this.poi.name}: INTERACTIVE mode - label can receive mouse events`);
        } else {
            // 🚫 Label is non-interactive - click-through to camera controls
            this._element.style.pointerEvents = 'none';
            this._element.style.cursor = 'default';
            //console.log(`👻 HTMLLabel ${this.poi.name}: NON-INTERACTIVE mode - mouse events pass through to camera`);
        }
    }

    /**
     * Apply opacity to the HTML element
     */
    protected applyOpacityToMaterial(opacity: number): void {
        if (this._element) {
            const currentElementOpacity = parseFloat(this._element.style.opacity || '1');
            
            if (Math.abs(currentElementOpacity - opacity) > 0.001) {
                this._element.style.opacity = opacity.toString();
            }
        }
    }

    public show(): void {
        this.setVisible(true);
    }

    public hide(): void {
        this.setVisible(false);
    }

    /**
     * Override onUpdate - simplified without redundant opacity handling
     */
    onUpdate(deltaTime: number): void {
        super.onUpdate(deltaTime); // This calls _updateOpacity which now handles opacity updates

        // Enforce fade-on-active for HTML labels (3D CSS) – show only active POI label
        if (!this._element) return;
        const appSettings = this.poi.getAppSettings();
        const fadeOnActive = this.poi.poidata.fadeLabelOnActive.value ?? (appSettings?.fadeLabelOnActive?.value ?? false);
        if (fadeOnActive) {
            const isActive = (this.poi as any).isActive === true;
            const wantVisible = isActive && (this.poi.poidata.showLabel.value !== false);
            const targetDisplay = wantVisible ? 'block' : 'none';
            if (this._element.style.display !== targetDisplay) {
                this._element.style.display = targetDisplay;
                this._element.style.opacity = wantVisible ? '1' : '0';
            }
        }
    }

    /**
     * Get the HTML element for CSS3D integration
     */
    getElement(): HTMLElement | null {
        return this._element;
    }

    /**
     * Create or update CSS3D object
     */
    createCSS3DObject(): CSS3DObject | null {
        if (!this._element) return null;
        
        // Create CSS3DObject if not already created
        if (!this._cssObject) {
            this._cssObject = new CSS3DObject(this._element);
            
            // Set initial properties
            this._cssObject.name = `HTMLLabel_${this.poi.name}`;
            
            // 🚫 REMOVED: Manual scaling - let LabelMesh._updateScale() handle this uniformly
            
            //console.log(`🌐 Created CSS3DObject for ${this.poi.name}`);
        }
        
        return this._cssObject;
    }

    /**
     * 🧪 EXPERIMENT: Try direct CSS3D scaling without base class dependency
     * Test if we can avoid super._updateScale() by calculating our own scaling
     */
    protected override _updateScale(): void {
        // 🧪 EXPERIMENT: Skip super._updateScale() and calculate our own scale
        // Since we now have default transform values, let's try direct calculation
        
        if (!this._cssObject) {
            console.warn(`❌ No CSS3DObject for scaling ${this.poi.name}`);
            return;
        }
        
        // Get label-specific scaling parameters
        const labelScale = this.poi.poidata.labelScale.value ?? 1.0;
        
        // Get actual DOM dimensions (similar to base class getCanvasWidth/Height)
        let elementWidth = this.getCanvasWidth();
        let elementHeight = this.getCanvasHeight();
        // Clamp tiny measurements to sane minimums for visibility
        if (elementWidth < 50) elementWidth = 400;
        if (elementHeight < 20) elementHeight = 200;
        
        // Calculate scale similar to base class but for CSS3D
        const BASE_SCALE = 0.000003; // Same as LabelMesh base class
        const CSS3D_SCALE_FACTOR = 25; // Calibrated for CSS3D visibility
        
        // Direct calculation: labelScale * baseScale * dimensions * css3dFactor
        const finalScaleX = labelScale * BASE_SCALE * elementWidth * CSS3D_SCALE_FACTOR;
        const finalScaleY = labelScale * BASE_SCALE * elementHeight * CSS3D_SCALE_FACTOR;
        
        // Apply to CSS3DObject
        this._cssObject.scale.set(finalScaleX, finalScaleY, 1);
        
        // Also update our transform for consistency (so UI shows correct values)
        this.transform.scale.value = new Vector3(
            labelScale * BASE_SCALE * elementWidth,
            labelScale * BASE_SCALE * elementWidth, 
            labelScale * BASE_SCALE * elementWidth
        );
        
        //console.log(`🧪 DIRECT CSS3D Scale: dims(${elementWidth}x${elementHeight}) → CSS3D(${finalScaleX.toFixed(8)}, ${finalScaleY.toFixed(8)}) → Transform(${this.transform.scale.value?.x.toFixed(3)}, ${this.transform.scale.value?.y.toFixed(3)})`);
    }

    /**
     * Get render type
     */
    getRenderType(): LabelMeshType.HTML {
        return LabelMeshType.HTML;
    }

    /**
     * Dispose resources
     */
    dispose(): void {
        // Dispose HTML-specific resources
        if (this._element) {
            // Remove from DOM if attached
            if (this._element.parentNode) {
                this._element.parentNode.removeChild(this._element);
            }
            this._element = null;
        }

        if (this._cssObject) {
            // Remove from CSS3D scene first
            this.removeFromScene();
            
            // Clean up CSS3DObject
            this._cssObject = null;
        }

        // Clean up mesh reference
        this._mesh = null;
        this._isInitialized = false;
        
        //console.log(`🗑️ HTMLLabelMesh disposed for ${this.poi.name}`);
    }

    /**
     * Override addToScene to use CSS3D scene instead of regular scene
     */
    protected addToScene(): void {
        if (!this._mesh || !Scene.Instance) return;
        
        // Use CSS3D scene method instead of regular scene
        Scene.Instance.addCSS3DObject(this._mesh);
        //console.log(`🌐 Added HTMLLabelMesh to CSS3D scene: ${this.poi.name}`);
    }

    /**
     * Override removeFromScene to use CSS3D scene
     */
    protected removeFromScene(): void {
        if (!this._mesh || !Scene.Instance) return;
        
        // Remove from CSS3D scene
        Scene.Instance.removeCSS3DObject(this._mesh);
        //console.log(`🗑️ Removed HTMLLabelMesh from CSS3D scene: ${this.poi.name}`);
    }

    /**
     * 🎯 SIMPLIFIED: Create standard 1x1 geometry - let LabelMesh._updateScale() handle proportions
     */
    protected createGeometry(): THREE.BufferGeometry {
        // 🎯 Always use 1x1 geometry - the base class scaling system handles proper proportions
        //console.log(`🔧 HTMLLabel: Using standard 1x1 geometry with base class scaling`);
        return new THREE.PlaneGeometry(1, 1);
    }

    /**
     * Returns the aspect ratio - for HTML labels, calculate from element
     */
    public getAspectRatio(): number {
        if (!this._element) {
            return 2.0; // Default fallback
        }
        
        // Force layout calculation if element is not yet measured
        if (this._element.offsetWidth === 0 || this._element.offsetHeight === 0) {
            // Temporarily add to DOM to measure
            document.body.appendChild(this._element);
            const rect = this._element.getBoundingClientRect();
            if (this._element.parentNode) {
                this._element.parentNode.removeChild(this._element);
            }
            
            if (rect.width > 0 && rect.height > 0) {
                const aspectRatio = rect.width / rect.height;
                //console.log(`🌐 Measured HTML element: ${rect.width}x${rect.height} = ${aspectRatio.toFixed(2)}`);
                return aspectRatio;
            }
        }
        
        // Try to get actual element dimensions
        const rect = this._element.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
            return rect.width / rect.height;
        }
        
        // Fallback to computed styles
        const computedStyle = window.getComputedStyle(this._element);
        const width = parseFloat(computedStyle.width) || 400;
        const height = parseFloat(computedStyle.height) || 200;
        
        return width / height;
    }

    /**
     * 🚫 MAJOR FIX: Removed updateGeometryFromContent() 
     * This was interfering with the LabelMesh._updateScale() system and causing verzerrung
     * 
     * The LabelMesh base class handles scaling properly using getCanvasWidth/Height()
     * CSS3DObject scale should NOT be manipulated manually - let the base system handle it
     */

    /**
     * Override updateContent to trigger geometry update
     */
    updateContent(text: string): void {
        // Call the original updateContent implementation
        if (!this._element || !this._isInitialized) {
            console.warn('HTMLLabelMesh not initialized');
            return;
        }

        try {
            // Convert Markdown to HTML using marked library
            const rawHtml = marked.parse(text) as string;
            
            // Sanitize HTML for security (removes dangerous scripts, etc.)
            const cleanHtml = DOMPurify.sanitize(rawHtml, {
                ALLOWED_TAGS: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'br', 'strong', 'em', 'b', 'i', 'u', 'code', 'pre', 'ul', 'ol', 'li', 'blockquote'],
                ALLOWED_ATTR: ['class']
            });

            this._element.innerHTML = cleanHtml;
            
            // 🚫 REMOVED: updateGeometryFromContent() - was causing scaling conflicts
            
        } catch (error) {
            console.error('Failed to convert markdown to HTML:', error);
            // Fallback to plain text
            this._element.textContent = text;
        }
    }

    /**
     * 🎯 IMPROVED: Get actual HTML element width for proper LabelMesh scaling
     */
    protected getCanvasWidth(): number {
        if (!this._element) {
            return 400; // Fallback for uninitialized elements
        }
        
        // Get actual rendered width from the HTML element
        const rect = this._element.getBoundingClientRect();
        if (rect.width > 0) {
            return rect.width;
        }
        
        // Fallback to computed style
        const computedStyle = window.getComputedStyle(this._element);
        const width = parseFloat(computedStyle.width) || 400;
        return width;
    }

    /**
     * 🎯 IMPROVED: Get actual HTML element height for proper LabelMesh scaling  
     */
    protected getCanvasHeight(): number {
        if (!this._element) {
            return 200; // Fallback for uninitialized elements
        }
        
        // Get actual rendered height from the HTML element
        const rect = this._element.getBoundingClientRect();
        if (rect.height > 0) {
            return rect.height;
        }
        
        // Fallback to computed style
        const computedStyle = window.getComputedStyle(this._element);
        const height = parseFloat(computedStyle.height) || 200;
        return height;
    }
} 